<script setup>
import { setDarkMode, useLayout } from '@/layout/composables/layout';
import { computed, onMounted, ref, watch } from 'vue';
import AppFooter from './AppFooter.vue';
import AppSidebar from './AppSidebar.vue';
import AppTopbar from './AppTopbar.vue';
import AnnouncementBanner from '@/components/AnnouncementBanner.vue';
import { stores } from '@/util/stores.js';

const { layoutConfig, layoutState, isSidebarActive, resetMenu } = useLayout();

const outsideClickListener = ref(null);

watch(
	() => stores.userSettingsStore.userSettings.darkMode,
	(enabled) => {
		// Keep the cached theme while the user's preferences are still loading.
		if (typeof enabled === 'boolean') setDarkMode(enabled);
	},
	{ immediate: true }
);

watch(
	() => stores.authStore.userInfo?.userNumber,
	(userNumber) => {
		if (userNumber == null) return;
		void stores.userSettingsStore.fetchUserSettings(userNumber).catch((error) => {
			console.error('AppLayout: failed to load user preferences', error);
		});
	},
	{ immediate: true }
);

onMounted(async () => {
	if (stores.authStore.isAuthenticated) {
		try {
			await stores.authStore.refreshProfileCompleteness();
		} catch (err) {
			console.error('AppLayout: refreshProfileCompleteness failed', err);
		}
	}
});

watch(isSidebarActive, (newVal) => {
	if (newVal) {
		bindOutsideClickListener();
	} else {
		unbindOutsideClickListener();
	}
});

/**
 * Pages that keep their tab and filters in the query string. Each view is keyed on its URL so it remounts on
 * navigation, but for these a query update is the page's own state: remounting reset Payouts from "Record a payment
 * made elsewhere" back to "Pay officials" and dropped selections and filters on every tab switch.
 */
const QUERY_STATE_PAGES = new Set(['Finance Payouts', 'Finance Ledger', 'Finance Settings', 'Finance Reports', 'Finance Invoices', 'profile']);
function viewKey(route) {
	return QUERY_STATE_PAGES.has(route.name) ? route.path : route.fullPath;
}

const mainContainerClass = computed(() => ({
	'lg:ml-[22rem]': layoutConfig.menuMode === 'static' && !layoutState.staticMenuDesktopInactive
}));

function bindOutsideClickListener() {
	if (!outsideClickListener.value) {
		outsideClickListener.value = (event) => {
			if (isOutsideClicked(event)) {
				resetMenu();
			}
		};
		document.addEventListener('click', outsideClickListener.value);
	}
}

function unbindOutsideClickListener() {
	if (outsideClickListener.value) {
		document.removeEventListener('click', outsideClickListener.value);
		outsideClickListener.value = null;
	}
}

function isOutsideClicked(event) {
	const sidebarEl = document.querySelector('.layout-sidebar');
	const topbarEl = document.querySelector('.layout-menu-button');

	return !(sidebarEl?.isSameNode(event.target) || sidebarEl?.contains(event.target) || topbarEl?.isSameNode(event.target) || topbarEl?.contains(event.target));
}
</script>

<template>
	<div>
		<div class="min-h-screen">
			<app-topbar />
			<app-sidebar />
			<div>
				<AnnouncementBanner />
				<div class="flex min-h-[calc(100vh-4rem)] flex-col justify-between px-8 pb-8 pt-4 transition-[margin] duration-200" :class="mainContainerClass">
					<div class="w-full min-[1960px]:mx-auto min-[1960px]:max-w-[1504px]">
						<router-view v-slot="{ Component }">
							<Transition
								enter-active-class="transition-[opacity,transform] duration-200 ease-in"
								leave-active-class="transition-[opacity,transform] duration-200 ease-in"
								enter-from-class="translate-y-2 opacity-0"
								leave-to-class="-translate-y-1 opacity-0"
								mode="out-in"
							>
								<component :is="Component" :key="viewKey($route)" />
							</Transition>
						</router-view>
					</div>
					<app-footer />
				</div>
			</div>
			<div v-if="layoutState.staticMenuMobileActive" class="fixed inset-0 z-[998] bg-[var(--maskbg)] lg:hidden" @click="resetMenu"></div>
		</div>
		<EGS_Toast />
	</div>
</template>
